import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { App, Button, Tooltip } from 'antd'
import {
  SettlementApiCode,
  SettlementApiError,
  SettlementStatus,
  getSettlementStatement,
  type SettlementStatement,
} from '@/api/settlements'
import { useTranslation } from 'react-i18next'
import {
  SETTLEMENT_BILLING_I18N,
  SETTLEMENT_STATUS_I18N,
  formatCny,
  formatConfirmAt,
  isServiceOngoing,
} from './format'

function SettlementDetail() {
  const { message } = App.useApp()
  const { t } = useTranslation()
  const { app_id = '', statementId = '' } = useParams()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const [row, setRow] = useState<SettlementStatement | null>(null)

  const back = () => {
    const qs = params.toString()
    navigate(`/apps/${app_id}/account/settlements${qs ? `?${qs}` : ''}`)
  }

  useEffect(() => {
    let cancelled = false
    void getSettlementStatement(statementId)
      .then((item) => {
        if (!cancelled) setRow(item)
      })
      .catch((err) => {
        if (cancelled) return
        if (err instanceof SettlementApiError && err.code === SettlementApiCode.NotFound) {
          message.error(t('settlements.noPermStatement'))
        } else {
          message.error(t('settlements.loadFail'))
        }
        back()
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statementId])

  if (!row) return null

  const copy = async (text: string) => {
    try {
      await navigator.clipboard?.writeText(text)
      message.success(t('settlements.copied'))
    } catch {
      message.error(t('settlements.loadFail'))
    }
  }

  return (
    <div className="flex flex-col gap-6 p-8 m-5 border border-line rounded-lg">
      <div>
        <Button type="link" className="px-0" onClick={back}>
          ← {t('settlements.backToList')}
        </Button>
        <h1 className="m-0 text-base font-medium">
          {t('settlements.detailTitle')} {row.statement_no}
        </h1>
      </div>
      <dl className="grid grid-cols-[140px_1fr] gap-x-4 gap-y-3 text-sm">
        <dt className="text-muted">{t('settlements.statementNo')}</dt>
        <dd className="m-0">
          <button
            type="button"
            className="border-none bg-transparent p-0 text-primary"
            onClick={() => void copy(row.statement_no)}
          >
            {row.statement_no}
          </button>
        </dd>
        <dt className="text-muted">{t('settlements.status')}</dt>
        <dd className="m-0">{t(SETTLEMENT_STATUS_I18N[row.status])}</dd>
        <dt className="text-muted">{t('settlements.orderNo')}</dt>
        <dd className="m-0">
          <Button type="link" className="px-0" onClick={() => message.error(t('settlements.noPermOrder'))}>
            {row.order_no}
          </Button>
        </dd>
        <dt className="text-muted">{t('settlements.appName')} / {t('settlements.productName')}</dt>
        <dd className="m-0">
          {row.app_name} / {row.product_name}
        </dd>
        <dt className="text-muted">{t('settlements.billing')}</dt>
        <dd className="m-0">{t(SETTLEMENT_BILLING_I18N[row.billing_type])}</dd>
        <dt className="text-muted">{t('settlements.spec')}</dt>
        <dd className="m-0">{row.spec}</dd>
        <dt className="text-muted">{t('settlements.serviceRange')}</dt>
        <dd className="m-0 inline-flex items-center gap-2">
          {row.service_start} ~ {row.service_end}
          {isServiceOngoing(row.service_end) && (
            <Tooltip title={t('settlements.ongoingTip')}>
              <span className="rounded bg-fill px-1.5 text-xs text-muted">{t('settlements.ongoing')}</span>
            </Tooltip>
          )}
        </dd>
        <dt className="text-muted">{t('settlements.settlementDate')}</dt>
        <dd className="m-0">{row.settlement_date}</dd>
        <dt className="text-muted">{t('settlements.amount')}</dt>
        <dd className="m-0">{formatCny(row.amount, true)}</dd>
        {row.status === SettlementStatus.Confirmed && row.confirmed_at && (
          <>
            <dt className="text-muted">{t('settlements.confirmedAt')}</dt>
            <dd className="m-0">{formatConfirmAt(row.confirmed_at)}</dd>
          </>
        )}
      </dl>
      <p className="m-0 text-xs text-muted">{t('settlements.withdrawNote')}</p>
    </div>
  )
}

export default SettlementDetail
